<template>
<StretchModal
  ref="modal"
  padding="0"
  maxWidth="640px"
  :borderColor="isPublic ? 'var(--pp10-orange)' : null"
  :headerColor="isPublic ? 'var(--pp10-orange-90)' : null"
  @close="onClose"
>
  <template #header>
    <span :class="{ 'orange-text': isPublic }">
      <span :class="isPublic ? 'icon-earth' : 'icon-lock'" />
      Folder Access &mdash; <b>{{ folderLabel }}</b>
    </span>
  </template>

  <section class="pa-2">

    <!-- Public / Private toggle -->
    <div class="flex-row flex-align-center flex-gap-1 access-toggle" :class="{ 'public': isPublic }">
      <ToggleButton
        :value="isPublic"
        :sync="true"
        :labels="{ checked: 'PUBLIC', unchecked: 'PRIVATE' }"
        :color="{ checked: 'var(--pp10-orange)', unchecked: '#4E98FF' }"
        :width="90"
        :height="26"
        @change="onToggle"
      />
      <div class="access-summary">
        <template v-if="isPublic">
          <span class="orange-text bold">PUBLIC</span> &mdash; anyone on the internet can view and download these files with no login.
        </template>
        <template v-else>
          <span class="bold">PRIVATE</span> &mdash; a login with matching privileges is required.
        </template>
      </div>
    </div>

    <!-- Public warning -->
    <div v-if="isPublic" class="public-warning mt-2">
      <span class="icon-warning" />
      Files placed in this folder are exposed on the public internet. Do not upload anything private here.
    </div>

    <!-- Private: who can access -->
    <div v-else class="mt-3">
      <div class="bold mb-1">Who can access this folder?</div>

      <label class="access-option" :class="{ 'active': grantMode === 'admins' }">
        <input type="radio" value="admins" v-model="grantMode" />
        <span><b>Admins only</b> <span class="opt-hint">&mdash; site administrators only (default)</span></span>
      </label>

      <label class="access-option" :class="{ 'active': grantMode === 'anyone' }">
        <input type="radio" value="anyone" v-model="grantMode" />
        <span><b>Any signed-in user</b> <span class="opt-hint">&mdash; anyone logged in, no specific privilege needed</span></span>
      </label>

      <label class="access-option" :class="{ 'active': grantMode === 'specific' }">
        <input type="radio" value="specific" v-model="grantMode" />
        <span><b>Specific privileges&hellip;</b> <span class="opt-hint">&mdash; logged in <b>and</b> holding any listed grant</span></span>
      </label>

      <!-- Specific-grant editor -->
      <div v-if="grantMode === 'specific'" class="specific-grants mt-2">
        <div class="hint mb-1">Search to add a grant, or type one and press Enter.</div>

        <div class="picker" ref="picker">
          <input
            type="text"
            class="w-100"
            v-model="search"
            placeholder="Search privileges (e.g. teacher, org:sub_user)…"
            @keyup.enter="addTyped"
            @keyup.esc.stop="closeSuggestions"
            @focus="showSuggestions = true"
          />
          <div v-if="showSuggestions && filteredFeatures.length" class="suggestions">
            <div
              v-for="f in filteredFeatures"
              :key="f.name"
              class="suggestion"
              @click="addGrant( f.name )"
            >
              <span class="suggestion-name">{{ f.name }}</span>
              <span v-if="f.description" class="suggestion-desc">{{ f.description }}</span>
            </div>
          </div>
        </div>

        <div class="grants mt-2">
          <div v-if="grants.length === 0" class="no-grants">
            <span class="icon-warning" />
            No grants added &mdash; this falls back to <b>admins only</b>.
          </div>
          <div v-for="grant in grants" :key="grant" class="grant-chip">
            <span class="grant-name">{{ grant }}</span>
            <span class="icon-cross remove" @click="removeGrant( grant )" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-row flex-justify-end mt-3">
      <button class="SquareBlueButton" :disabled="saving" @click="attemptSave">Save</button>
    </div>
  </section>

  <!-- Confirmation required when EXPOSING a folder to the public -->
  <ConfirmDialog
    ref="confirmPublicModal"
    confirm="Make Public"
    @confirm="save"
  >
    <div class="center">
      <div class="icon-warning orange-text" style="font-size: 2.5em;" />
      <div class="font-size-1-3 bold mt-1">Make <span class="orange-text">{{ folderLabel }}</span> public?</div>
      <div class="mt-1">Every file in this folder will be downloadable by <b>anyone on the internet</b>, with no login required.</div>
    </div>
  </ConfirmDialog>

</StretchModal>
</template>


<script>
import StretchModal from '@/components/utilities/StretchModal.vue'
import ConfirmDialog from '@/components/utilities/ConfirmDialog.vue'
import RolesAPI from '@/api/RolesAPI'
import { ToggleButton } from 'vue-js-toggle-button'

export default {
  name: 'folder-access-modal',

  components: {
    StretchModal,
    ConfirmDialog,
    ToggleButton,
  },

  props: {
    // Display name of the folder being edited (e.g. the current folder name or 'Home')
    folderLabel: {
      type: String,
      default: 'this folder',
    },
  },

  data() {
    return {
      isPublic: false,
      grantMode: 'admins',   // 'admins' (site_admin:*) | 'anyone' (*) | 'specific'
      grants: [],            // the free-text list, used only in 'specific' mode
      wasPublic: false,      // access state when the modal was opened (to detect exposure)

      search: '',
      showSuggestions: false,
      saving: false,
    }
  },

  mounted() {
    document.addEventListener( 'mousedown', this.onDocumentMousedown )
  },

  beforeDestroy() {
    document.removeEventListener( 'mousedown', this.onDocumentMousedown )
  },

  computed: {
    features() {
      // Loaded application-wide into the store; fall back to empty until fetched.
      const set = this.$store.state.Features
      return set && set.objects ? set.objects : []
    },

    filteredFeatures() {
      const term = this.search.trim().toLowerCase()
      const chosen = new Set( this.grants )
      return this.features
        .filter( f => !chosen.has( f.name ) )
        .filter( f => !term || f.name.toLowerCase().includes( term ) || ( f.description || '' ).toLowerCase().includes( term ) )
        .slice( 0, 12 )
    },
  },

  methods: {
    // Opens the modal seeded with the folder's current access record.
    open( access, grants ) {
      this.isPublic = access === 'public'
      this.wasPublic = this.isPublic

      const list = Array.isArray( grants ) ? grants.filter( Boolean ) : []
      this.grantMode = this.deriveMode( list )
      // Keep the raw list for the 'specific' editor; the two special tokens map
      // onto the radio choices instead of appearing as chips.
      this.grants = this.grantMode === 'specific' ? [ ...list ] : []

      this.search = ''
      this.showSuggestions = false
      this.saving = false
      this.ensureFeatures()
      this.$refs.modal.open()
    },

    // Map a stored grants array onto one of the three radio choices.
    // Server default for a private folder with no grants is ['site_admin:*'].
    deriveMode( list ) {
      if( list.length === 0 ) return 'admins'
      if( list.length === 1 && list[0] === 'site_admin:*' ) return 'admins'
      if( list.length === 1 && list[0] === '*' ) return 'anyone'
      return 'specific'
    },

    async ensureFeatures() {
      if( this.features.length > 0 ) return
      try {
        this.$store.state.Features.objects = await RolesAPI.getFeatures()
      } catch ( e ) {
        console.error( 'Failed to load feature list for grant picker:', e.message )
      }
    },

    onToggle( e ) {
      this.isPublic = e.value
    },

    closeSuggestions() {
      this.showSuggestions = false
    },

    // Close the suggestion dropdown when clicking anywhere outside the picker.
    onDocumentMousedown( e ) {
      if( !this.showSuggestions ) return
      const picker = this.$refs.picker
      if( picker && !picker.contains( e.target ) ) this.showSuggestions = false
    },

    addGrant( name ) {
      const grant = ( name || '' ).trim()
      if( grant && !this.grants.includes( grant ) ) this.grants.push( grant )
      this.search = ''
    },

    addTyped() {
      if( this.search.trim() ) this.addGrant( this.search )
    },

    removeGrant( grant ) {
      this.grants = this.grants.filter( g => g !== grant )
    },

    // Route exposure (private -> public) through a confirmation; everything else saves directly.
    attemptSave() {
      if( this.isPublic && !this.wasPublic ) {
        this.$refs.confirmPublicModal.open()
        return
      }
      this.save()
    },

    save() {
      const access = this.isPublic ? 'public' : 'private'
      this.saving = true
      this.$emit( 'save', { access, grants: this.resolveGrants() } )
    },

    // Translate the current UI state into the grants array the server expects.
    resolveGrants() {
      if( this.isPublic ) return []
      if( this.grantMode === 'admins' ) return [ 'site_admin:*' ]
      if( this.grantMode === 'anyone' ) return [ '*' ]
      // 'specific' — trim blanks & de-dupe. Empty ⇒ [] (server falls back to admins-only).
      return [ ...new Set( this.grants.map( g => g.trim() ).filter( Boolean ) ) ]
    },

    // Called by the parent once the save round-trips successfully.
    close() {
      this.$refs.modal.close()
    },

    onClose() {
      this.showSuggestions = false
    },
  },
}
</script>


<style scoped>
.orange-text { color: var(--pp10-orange-40); }
.bold { font-weight: bold; }

.access-toggle {
  padding: 0.75em 1em;
  border-radius: 0.5em;
  background-color: var(--ekno-blue-95, #eef4ff);
  border: 1px solid var(--ekno-blue-90, #d7e6ff);
}
.access-toggle.public {
  background-color: var(--pp10-orange-95);
  border: 1px solid var(--pp10-orange-80);
}
.access-summary { line-height: 1.3; }

.public-warning {
  background-color: var(--pp10-orange-90);
  border-left: 4px solid var(--pp10-orange);
  padding: 0.75em 1em;
  border-radius: 0.25em;
  color: var(--pp10-orange-30);
}

.hint { color: #666; font-size: 0.9em; line-height: 1.35; }

.access-option {
  display: flex;
  align-items: flex-start;
  gap: 0.6em;
  padding: 0.6em 0.75em;
  border: 1px solid #ddd;
  border-radius: 0.4em;
  margin-bottom: 0.4em;
  cursor: pointer;
}
.access-option:hover { background-color: #f7f9fc; }
.access-option.active {
  border-color: var(--ekno-blue);
  background-color: var(--ekno-blue-95, #eef4ff);
}
.access-option input { margin-top: 0.2em; }
.opt-hint { color: #777; font-size: 0.9em; }

.picker { position: relative; }
.picker input {
  padding: 0.5em;
  border: 1px solid #ccc;
  border-radius: 0.25em;
  box-sizing: border-box;
}
.suggestions {
  position: absolute;
  z-index: 5;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0 0 0.25em 0.25em;
  box-shadow: 0 6px 16px -6px #999;
  max-height: 16em;
  overflow-y: auto;
}
.suggestion {
  padding: 0.4em 0.6em;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.suggestion:hover { background-color: var(--ekno-blue-95, #eef4ff); }
.suggestion-name { font-family: monospace; font-weight: bold; }
.suggestion-desc { display: block; font-size: 0.8em; color: #777; }

.grants { display: flex; flex-wrap: wrap; gap: 0.5em; }
.grant-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  background-color: var(--ekno-blue-90, #d7e6ff);
  border-radius: 1em;
  padding: 0.25em 0.4em 0.25em 0.8em;
}
.grant-name { font-family: monospace; }
.grant-chip .remove { cursor: pointer; color: #555; }
.grant-chip .remove:hover { color: var(--pp10-red, darkred); }

.no-grants {
  color: var(--pp10-orange-30);
  background-color: var(--pp10-orange-95);
  border-radius: 0.25em;
  padding: 0.5em 0.75em;
  width: 100%;
}
</style>
